/**
 * Um deploy, quatro domínios.
 *
 * myard.bio, myard.info e myard.shop servem o mesmo aplicativo que o
 * yardsuite.com.br. A diferença é o que a raiz de cada um significa:
 *
 *   yardsuite.com.br/...      painel, login, tudo o que pede conta
 *   myard.bio/cima            a página Bio da CIMA, sem prefixo /b/
 *   myard.info/cima           o cartão de visitas
 *   myard.shop/cima           a loja
 *   myard.bio  (raiz, só)     vai para yardsuite.com.br
 *
 * A referência do cliente é o handle, que já é único em toda a base. Por isso
 * `myard.bio/cima` não precisa de tabela nova: o caminho é o handle.
 *
 * O host não é autoridade de nada. Ele escolhe qual página montar; quem decide
 * se aquele cliente tem direito àquela superfície é a RLS, na `bio_publico`.
 */

export type Superficie = 'bio' | 'info' | 'shop';

/** Domínio público -> superfície que ele serve. */
export const DOMINIOS: Record<string, Superficie> = {
  'myard.bio': 'bio',
  'myard.info': 'info',
  'myard.shop': 'shop',
};

/** Onde o painel mora. A raiz dos domínios públicos manda para cá. */
export const PAINEL = 'https://yardsuite.com.br';

/** Prefixo interno equivalente, usado no painel e na prévia. */
export const PREFIXO: Record<Superficie, string> = {
  bio: '/b',
  info: '/i',
  shop: '/s',
};

const semWww = (host: string) => host.replace(/^www\./i, '').toLowerCase();

/**
 * Qual superfície este host serve, ou null se for o painel.
 * Aceita subdomínio de teste (ex: homolog.myard.bio) para não travar staging.
 */
export function superficieDoHost(host: string): Superficie | null {
  const h = semWww(host).split(':')[0] ?? '';
  const direto = DOMINIOS[h];
  if (direto) return direto;
  for (const [dominio, superficie] of Object.entries(DOMINIOS)) {
    if (h.endsWith(`.${dominio}`)) return superficie;
  }
  return null;
}

/** Onde as portas dos clientes moram. */
export const DOMINIO_PAINEL = 'yardsuite.com.br';

/**
 * Subdomínios de `yardsuite.com.br` que já têm sentido próprio - nunca são
 * slug de cliente. `mail` é o suitemail; `meet` é o Jitsi self-hosted (nem
 * passa por este app, mora em `jitsi_dominio` por organização); `admin` fica
 * reservado para não colidir com `/admin` do console. O resto é a lista do
 * Hub (`api`, `app`, `webmail`, `autodiscover`, `ftp`): nomes que o provedor
 * de e-mail e de hospedagem usam, e que não podem virar porta de cliente.
 *
 * Mesma lista no banco: `public.slug_reservado` (0149), que barra a criação
 * e a troca de slug.
 */
export const SUBDOMINIOS_RESERVADOS: ReadonlySet<string> = new Set([
  'www', 'mail', 'api', 'admin', 'app', 'webmail', 'autodiscover', 'ftp', 'meet',
]);

/** `true` quando o slug é um subdomínio de serviço (ver acima). */
export const slugReservado = (slug: string) => SUBDOMINIOS_RESERVADOS.has(slug.trim().toLowerCase());

/**
 * `true` em `mail.yardsuite.com.br` (sem cliente, a organização ativa vem da
 * sessão) e em `{cliente}.mail.yardsuite.com.br` (com cliente na URL, o
 * endereço que a VPS nova serve com vhost curinga). O primeiro formato fica
 * de pé por retrocompatibilidade - link já mandado não se quebra.
 */
export const ehSubdominioDeMail = (host: string) => {
  const h = semWww(host).split(':')[0] ?? '';
  return h === `mail.${DOMINIO_PAINEL}` || h.endsWith(`.mail.${DOMINIO_PAINEL}`);
};

/**
 * O slug do cliente quando o host é a porta dele do suitemail:
 * `cima.mail.yardsuite.com.br` -> `cima`. `null` em `mail.yardsuite.com.br`
 * puro (sem cliente na URL) e em qualquer host que não seja subdomínio de
 * mail - espelha `clienteDoHost`, mas para este outro produto.
 */
export function clienteDoSubdominioDeMail(host: string): string | null {
  const h = semWww(host).split(':')[0] ?? '';
  const sufixo = `.mail.${DOMINIO_PAINEL}`;
  if (!h.endsWith(sufixo)) return null;
  const slug = h.slice(0, -sufixo.length);
  return slug && !slug.includes('.') ? slug : null;
}

/**
 * Para onde vai um endereço antigo do suitemail, ou `null` se ele fica onde está.
 *
 * O suitemail tem uma porta só, `yardsuite.com.br/mail`, sem slug. Os outros
 * endereços que já foram mandados (`mail.yardsuite.com.br` e
 * `{cliente}.yardsuite.com.br/mail`) são levados para lá, e o caminho que
 * vem depois de `/mail` vai junto: `mail.yardsuite.com.br/config` vira
 * `yardsuite.com.br/mail/config`.
 *
 * `{cliente}.yardsuite.com.br` sem `/mail` NÃO redireciona: é a porta do
 * painel daquele cliente, e continua sendo.
 */
export function destinoDoSuitemailNoApex(
  host: string,
  caminho: string,
  busca = '',
  fragmento = '',
): string | null {
  const h = semWww(host).split(':')[0] ?? '';

  if (ehSubdominioDeMail(host)) {
    const resto = caminho === '/' || caminho === '' ? '' : caminho;
    return `${PAINEL}/mail${resto}${busca}${fragmento}`;
  }

  const ehDoPainel = h.endsWith(`.${DOMINIO_PAINEL}`) && clienteDoHost(host) !== null;
  const pedeMail = caminho === '/mail' || caminho.startsWith('/mail/');
  if (ehDoPainel && pedeMail) return `${PAINEL}${caminho}${busca}${fragmento}`;

  return null;
}

/** Endereço do suitemail de um cliente, para mostrar e copiar no painel -
 *  espelha `enderecoDoCliente`, mas para `{cliente}.mail.yardsuite.com.br`. */
export const enderecoMailDoCliente = (slug: string) => `https://${slug}.mail.${DOMINIO_PAINEL}`;

/**
 * O endereço de uma REUNIÃO, e ele é o mesmo para todo cliente.
 *
 * `meet.${DOMINIO_PAINEL}` é o servidor Jitsi, e desde 02/09/2026 o nginx
 * dele também atende `/reuniao/` e `/sala/` na aplicação (ver
 * `docs/forensics/INFRA.md`). O link de reunião passou para cá por um
 * motivo prático: ele é ditado por telefone. "meet ponto yardsuite ponto
 * com ponto br, barra reunião, 064-4103" cabe numa frase;
 * "weareyard ponto mail ponto yardsuite..." não cabe.
 *
 * Não tem slug de propósito. O código da sala já identifica a reunião, e
 * quem entra por ele é convidado - o tenant de quem convidou não muda
 * nada para ele, e tirar o nome do cliente do link tira também a dica de
 * com quem a pessoa vai falar, que não é da conta de quem passa o olho.
 */
export const enderecoDeReuniao = () => `https://meet.${DOMINIO_PAINEL}`;

/**
 * `true` só na raiz nua do painel (`yardsuite.com.br`, com ou sem `www`) - o
 * único lugar de onde faz sentido pular para o subdomínio do cliente depois
 * do login. Em `localhost` (sem vhost curinga), em `{slug}.yardsuite.com.br`
 * (já é lá) e nos subdomínios reservados (`mail`, `meet`, `admin`) não pula.
 */
export const ehRaizDoPainel = (host: string) => semWww(host).split(':')[0] === DOMINIO_PAINEL;

/**
 * `true` em `localhost` (com ou sem subdomínio de teste, `cima.localhost`) -
 * o único lugar sem vhost curinga de verdade, onde o caminho por slug
 * (`/cima/home`) continua sendo o único jeito de testar a área do cliente.
 * Em produção esse caminho fica desligado de propósito - ver `Router.tsx`,
 * o pedido foi matar `yardsuite.com.br/{cliente}/...` fora do dev.
 */
export const ehLocalhost = (host: string) => {
  const h = semWww(host).split(':')[0] ?? '';
  return h === 'localhost' || h.endsWith('.localhost');
};

/**
 * O slug do cliente, quando o host é a porta dele: `cima.yardsuite.com.br`.
 *
 * `www`, o próprio painel, `mail`/`meet`/`admin` e os domínios públicos do
 * Bio não contam: nesses o subdomínio já significa outra coisa. Em
 * desenvolvimento aceita `cima.localhost`, senão não há como testar a tela
 * sem DNS.
 */
export function clienteDoHost(host: string): string | null {
  const h = semWww(host).split(':')[0] ?? '';
  if (emDominioPublico(h)) return null;

  for (const base of [DOMINIO_PAINEL, 'localhost']) {
    if (h.endsWith(`.${base}`)) {
      const slug = h.slice(0, -(base.length + 1));
      if (slug && !slug.includes('.') && !SUBDOMINIOS_RESERVADOS.has(slug)) return slug;
    }
  }
  return null;
}

/** Endereço de entrada de um cliente. Espelha `endereco_do_cliente` no banco. */
export const enderecoDoCliente = (slug: string) => `https://${slug}.${DOMINIO_PAINEL}`;

/** true quando estamos num dos domínios públicos. */
export const emDominioPublico = (host: string) => superficieDoHost(host) !== null;

/** Endereço público de um cliente, para mostrar e copiar no painel. */
export function enderecoPublico(superficie: Superficie, handle: string): string {
  const dominio = dominioDe(superficie);
  return dominio ? `https://${dominio}/${handle}` : `${PAINEL}${PREFIXO[superficie]}/${handle}`;
}

export const dominioDe = (superficie: Superficie): string | undefined =>
  Object.entries(DOMINIOS).find(([, s]) => s === superficie)?.[0];

/** Letra do prefixo interno -> superfície. `/b/cima` é a Bio da cima. */
const POR_LETRA: Record<string, Superficie> = { b: 'bio', i: 'info', s: 'shop' };

/**
 * Para onde um link interno deve apontar, visto de onde estamos.
 *
 * As telas públicas navegam entre si por rota interna (`/b/cima`, `/s/cima/carrinho`).
 * No painel isso é o endereço certo. Mas em myard.bio o domínio já diz a
 * superfície, então repetir o prefixo deixa `myard.bio/b/cima` na barra: feio,
 * duplicado para o buscador, e não é o link que o cliente divulga.
 *
 *   painel      /b/cima         -> /b/cima
 *   myard.bio   /b/cima         -> /cima
 *   myard.shop  /s/cima/carrinho-> /cima/carrinho
 *   myard.bio   /s/cima/p/x     -> https://myard.shop/cima/p/x
 *
 * O último caso é o que exige cuidado: link de outra superfície não pode virar
 * caminho relativo, senão o domínio errado tenta montar a página errada.
 */
export function hrefPublico(interno: string, host: string): string {
  const m = /^\/([bis])(\/.*)?$/.exec(interno);
  if (!m) return interno;

  const alvo = POR_LETRA[m[1] as string];
  const resto = m[2] ?? '';
  const atual = superficieDoHost(host);

  if (!alvo || atual === null) return interno;
  if (atual === alvo) return resto || '/';

  const dominio = dominioDe(alvo);
  return dominio ? `https://${dominio}${resto}` : interno;
}

